/**
 * BidAwardPanel — mark a Bid Desk record won and hand it to the Project
 * Command Center through a review-and-confirm conversion sheet.
 *
 * Works for both record types: agency/GC solicitations and notes/intake
 * records (both live in bid_solicitations).
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Trophy, Loader2, ArrowRightCircle, CheckCircle2, Plus, Trash2, ExternalLink, XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { API } from "@/App";

const money = (v) => `$${(Number(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

export default function BidAwardPanel({ record, token, onChanged }) {
  const headers = { Authorization: `Bearer ${token}` };
  const rid = record?.id;
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showWon, setShowWon] = useState(false);
  const [showConvert, setShowConvert] = useState(false);
  const [won, setWon] = useState({ award_amount: "", award_date: "", awarded_by: "", notes: "" });
  const [form, setForm] = useState(null);
  const [opts, setOpts] = useState({ pm_email: "", notify_pm: true, push_bom_to_sourcing: false, carry_surveys: true });
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    if (!rid) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/bid-desk/records/${rid}/award-preview`, { headers });
      setPreview(r.data);
    } catch { /* quiet */ } finally { setLoading(false); }
  }, [rid, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const markWon = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/bid-desk/records/${rid}/mark-won`, {
        award_amount: Number(won.award_amount) || preview?.economics?.contract_value || 0,
        award_date: won.award_date, awarded_by: won.awarded_by, notes: won.notes,
      }, { headers });
      toast.success("Logged as won — now convert it to a project");
      setShowWon(false);
      await load();
      onChanged?.();
      openConvert();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not mark it won"); } finally { setBusy(false); }
  };

  const markLost = async () => {
    const reason = window.prompt("Why did we lose it? (optional)") ?? null;
    if (reason === null) return;
    try {
      await axios.post(`${API}/bid-desk/records/${rid}/mark-lost`, { reason }, { headers });
      toast.success("Closed out as lost");
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not close it out"); }
  };

  const openConvert = () => {
    const p = preview?.prefill;
    if (!p) { toast.error("Still loading the conversion sheet"); return; }
    setForm({
      name: p.name, description: p.description, hospital_name: p.hospital_name,
      facility_name: p.facility_name, project_type: p.project_type, priority: p.priority,
      budget: p.budget, start_date: (p.start_date || "").slice(0, 10),
      target_end_date: (p.target_end_date || "").slice(0, 10),
      scope_of_work: p.scope_of_work, department: "",
      contact_name: p.contact_name, contact_email: p.contact_email, contact_phone: p.contact_phone,
      compliance_requirements: p.compliance_requirements || [],
      phases: (p.phases || []).map((x) => ({ ...x })),
      cost_line_items: p.cost_line_items || [],
    });
    setShowConvert(true);
  };

  const convert = async () => {
    const pm = (preview?.project_managers || []).find((u) => u.email === opts.pm_email);
    setBusy(true);
    try {
      const r = await axios.post(`${API}/bid-desk/records/${rid}/convert-to-command-project`, {
        ...form,
        budget: Number(form.budget) || 0,
        pm_id: pm?.id || null, pm_name: pm?.name || "", pm_email: pm?.email || "",
        notify_pm: opts.notify_pm && !!pm?.email,
        push_bom_to_sourcing: opts.push_bom_to_sourcing,
        carry_surveys: opts.carry_surveys,
      }, { headers });
      setResult(r.data);
      setShowConvert(false);
      toast.success(`Project ${r.data.project_id} created in the Project Command Center`);
      if (opts.notify_pm && pm?.email && !r.data.pm_notified) {
        toast.error(`PM email did not go out: ${r.data.notify_error || "mail provider rejected it"}`);
      }
      await load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Conversion failed"); } finally { setBusy(false); }
  };

  if (!rid) return null;
  const converted = preview?.record?.converted_project_id || record?.converted_project_id;
  const status = preview?.record?.status || record?.status;
  const econ = preview?.economics || {};

  return (
    <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50/30" data-testid="bid-award-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap p-4">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.14em] text-emerald-700">
            Award → fulfillment
          </p>
          <h3 className="text-base font-black text-slate-900 inline-flex items-center gap-2">
            <Trophy className="w-4 h-4 text-emerald-700" /> Win this bid
          </h3>
          <p className="text-xs text-slate-600 max-w-xl">
            When the owner says yes, this hands the scope, BOM, standards and site survey straight
            into the Project Management window as a live project.
          </p>
        </div>

        {converted ? (
          <div className="text-right" data-testid="bid-converted-badge">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-black uppercase tracking-wide bg-emerald-600 text-white">
              <CheckCircle2 className="w-3.5 h-3.5" /> Converted → {converted}
            </span>
            <p className="text-[11px] text-slate-500 mt-1">
              {preview?.record?.converted_project_name || ""}
            </p>
            <a href="/admin?tab=projects" className="text-[11px] text-emerald-700 hover:underline inline-flex items-center gap-1 mt-0.5">
              Open in Project Management <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            {status === "lost" ? (
              <span className="text-[11px] font-black uppercase tracking-wide text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-2.5 py-1">
                Closed — lost
              </span>
            ) : null}
            <Button size="sm" variant="outline" onClick={markLost} data-testid="bid-mark-lost-btn">
              <XCircle className="w-3.5 h-3.5 mr-1" /> Lost
            </Button>
            {status === "awarded" ? (
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={openConvert}
                disabled={loading} data-testid="bid-convert-btn">
                <ArrowRightCircle className="w-3.5 h-3.5 mr-1" /> Convert to project
              </Button>
            ) : (
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700"
                onClick={() => {
                  setWon((w) => ({ ...w, award_amount: econ.contract_value || "" }));
                  setShowWon(true);
                }} disabled={loading} data-testid="bid-mark-won-btn">
                {loading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Trophy className="w-3.5 h-3.5 mr-1" />}
                Mark as won
              </Button>
            )}
          </div>
        )}
      </div>

      {preview && !converted && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 px-4 pb-4" data-testid="bid-award-economics">
          {[
            ["BOM sell", money(econ.bom_sell)],
            ["Committed subcontract", money(econ.committed_subcontract)],
            ["Contract value", money(econ.contract_value)],
            ["Phases ready", `${(preview.prefill?.phases || []).length}`],
          ].map(([k, v]) => (
            <div key={k} className="rounded-lg border border-emerald-200 bg-white p-2.5">
              <p className="text-[9px] font-black uppercase tracking-wider text-slate-400">{k}</p>
              <p className="text-sm font-black text-slate-900">{v}</p>
            </div>
          ))}
        </div>
      )}

      {result && (
        <div className="mx-4 mb-4 rounded-lg border border-emerald-300 bg-white p-3" data-testid="bid-convert-result">
          <p className="text-sm font-bold text-emerald-800">
            Project {result.project_id} created — {result.phases?.length || 0} phases.
          </p>
          <p className="text-[11px] text-slate-600 mt-0.5">
            {result.surveys_carried} site survey(s) carried over
            {result.sourcing_request_id ? ` · Sourcing request ${result.sourcing_request_id} opened from the BOM` : ""}
            {result.pm_notified ? " · PM notified by email" : result.notify_error ? ` · PM email failed: ${result.notify_error}` : ""}
          </p>
        </div>
      )}

      {/* mark won */}
      <Dialog open={showWon} onOpenChange={setShowWon}>
        <DialogContent className="max-w-md" data-testid="bid-won-dialog">
          <DialogHeader><DialogTitle>Record the award</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Input type="number" placeholder="Award amount" value={won.award_amount}
              onChange={(e) => setWon((w) => ({ ...w, award_amount: e.target.value }))}
              data-testid="bid-won-amount" />
            <Input type="date" value={won.award_date}
              onChange={(e) => setWon((w) => ({ ...w, award_date: e.target.value }))}
              data-testid="bid-won-date" />
            <Input placeholder="Who awarded it (owner / GC contact)" value={won.awarded_by}
              onChange={(e) => setWon((w) => ({ ...w, awarded_by: e.target.value }))}
              data-testid="bid-won-by" />
            <Textarea rows={2} placeholder="Award notes — PO number, verbal vs written, conditions…"
              value={won.notes} onChange={(e) => setWon((w) => ({ ...w, notes: e.target.value }))}
              data-testid="bid-won-notes" />
            <div className="flex justify-end">
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={markWon} disabled={busy}
                data-testid="bid-won-save">
                {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Trophy className="w-3.5 h-3.5 mr-1" />}
                Mark won &amp; review conversion
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* conversion review sheet */}
      <Dialog open={showConvert} onOpenChange={setShowConvert}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="bid-convert-dialog">
          <DialogHeader>
            <DialogTitle>Convert to project — review before it goes live</DialogTitle>
          </DialogHeader>
          {form && (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-2">
                <div className="sm:col-span-2">
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Project name</label>
                  <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    data-testid="convert-name" />
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Owner / health system</label>
                  <Input value={form.hospital_name} onChange={(e) => setForm((f) => ({ ...f, hospital_name: e.target.value }))}
                    data-testid="convert-hospital" />
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Facility</label>
                  <Input value={form.facility_name} onChange={(e) => setForm((f) => ({ ...f, facility_name: e.target.value }))}
                    data-testid="convert-facility" />
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Contract value</label>
                  <Input type="number" value={form.budget}
                    onChange={(e) => setForm((f) => ({ ...f, budget: e.target.value }))} data-testid="convert-budget" />
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Priority</label>
                  <select value={form.priority} className="h-10 w-full text-sm border border-slate-300 rounded-md px-2"
                    onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))} data-testid="convert-priority">
                    {["critical", "high", "medium", "low"].map((x) => <option key={x} value={x}>{x}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Start date</label>
                  <Input type="date" value={form.start_date}
                    onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))} data-testid="convert-start" />
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Target completion</label>
                  <Input type="date" value={form.target_end_date}
                    onChange={(e) => setForm((f) => ({ ...f, target_end_date: e.target.value }))} data-testid="convert-end" />
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Owner contact</label>
                  <Input value={form.contact_name} onChange={(e) => setForm((f) => ({ ...f, contact_name: e.target.value }))}
                    data-testid="convert-contact-name" />
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Owner contact email</label>
                  <Input value={form.contact_email} onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))}
                    data-testid="convert-contact-email" />
                </div>
              </div>

              {/* PM */}
              <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">
                  Assign the Project Manager
                </label>
                <select value={opts.pm_email} className="h-10 w-full text-sm border border-slate-300 rounded-md px-2 mt-1 bg-white"
                  onChange={(e) => setOpts((o) => ({ ...o, pm_email: e.target.value }))} data-testid="convert-pm">
                  <option value="">— unassigned for now —</option>
                  {(preview?.project_managers || []).map((u) => (
                    <option key={u.email} value={u.email}>{u.name || u.email} ({u.role})</option>
                  ))}
                </select>
                <div className="mt-2 space-y-1.5">
                  <label className="flex items-center gap-2 text-xs text-slate-700">
                    <input type="checkbox" checked={opts.notify_pm} data-testid="convert-notify-pm"
                      onChange={(e) => setOpts((o) => ({ ...o, notify_pm: e.target.checked }))} />
                    Email the PM the project brief and kickoff phases
                  </label>
                  <label className="flex items-center gap-2 text-xs text-slate-700">
                    <input type="checkbox" checked={opts.push_bom_to_sourcing} data-testid="convert-push-bom"
                      onChange={(e) => setOpts((o) => ({ ...o, push_bom_to_sourcing: e.target.checked }))} />
                    Open a Sourcing Desk request from the BOM ({(preview?.bom || []).length} line(s)) so it can go out for supplier quotes
                  </label>
                  <label className="flex items-center gap-2 text-xs text-slate-700">
                    <input type="checkbox" checked={opts.carry_surveys} data-testid="convert-carry-surveys"
                      onChange={(e) => setOpts((o) => ({ ...o, carry_surveys: e.target.checked }))} />
                    Carry the {(preview?.surveys || []).length} pre-bid site survey(s) onto the project
                  </label>
                </div>
              </div>

              {/* phases */}
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">
                    Kickoff phases ({form.phases.length})
                  </label>
                  <Button size="sm" variant="outline" data-testid="convert-add-phase"
                    onClick={() => setForm((f) => ({ ...f, phases: [...f.phases, { name: "", lead_time_weeks: 2, compliance_tags: [], budget: 0 }] }))}>
                    <Plus className="w-3.5 h-3.5 mr-1" /> Phase
                  </Button>
                </div>
                <div className="space-y-1.5 mt-1.5">
                  {form.phases.map((ph, i) => (
                    <div key={i} className="flex items-center gap-2" data-testid={`convert-phase-${i}`}>
                      <Input className="h-9 text-xs" value={ph.name} placeholder="Phase name"
                        onChange={(e) => setForm((f) => ({ ...f, phases: f.phases.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) }))} />
                      <Input className="h-9 text-xs w-24" type="number" value={ph.lead_time_weeks}
                        onChange={(e) => setForm((f) => ({ ...f, phases: f.phases.map((x, j) => (j === i ? { ...x, lead_time_weeks: e.target.value } : x)) }))} />
                      <span className="text-[10px] text-slate-400 w-8">wks</span>
                      <Button size="sm" variant="ghost" className="text-red-500 hover:bg-red-50"
                        onClick={() => setForm((f) => ({ ...f, phases: f.phases.filter((_, j) => j !== i) }))}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              {!!(form.compliance_requirements || []).length && (
                <div>
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">
                    Standards carried from the Standards Compass
                  </label>
                  <div className="flex flex-wrap gap-1.5 mt-1" data-testid="convert-standards">
                    {form.compliance_requirements.map((c) => (
                      <span key={c} className="text-[10px] font-bold text-sky-800 bg-sky-50 border border-sky-200 rounded px-1.5 py-0.5">{c}</span>
                    ))}
                  </div>
                </div>
              )}

              <div className="rounded-lg border border-slate-200 p-3 text-xs text-slate-600">
                <strong>{(form.cost_line_items || []).length}</strong> cost line item(s) will land on
                the project cost worksheet, totalling{" "}
                <strong>{money((form.cost_line_items || []).reduce((s, x) => s + (Number(x.retail_price) || 0), 0))}</strong> sell
                against <strong>{money((form.cost_line_items || []).reduce((s, x) => s + (Number(x.subcontractor_cost) || 0), 0))}</strong> cost.
              </div>

              <div className="flex justify-end gap-2">
                <Button size="sm" variant="outline" onClick={() => setShowConvert(false)}>Cancel</Button>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={convert} disabled={busy}
                  data-testid="convert-confirm-btn">
                  {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <ArrowRightCircle className="w-3.5 h-3.5 mr-1" />}
                  Create the project
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
